import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, HandHelping, GraduationCap, Sprout, Home as HomeIcon, DollarSign, AlertTriangle, ArrowUpRight } from "lucide-react";
import { APPEALS } from "@/data/appeals";
import { useArticles } from "@/lib/useArticles";
import PublicSubmissionForm from "@/components/PublicSubmissionForm";

const WAYS = [
  { to: "/opportunities", icon: HandHelping, title: "Volunteer time", body: "Serve meals, tutor, build, mentor. Log hours and earn Social Capital." },
  { to: "/organizations", icon: Heart, title: "Support an organization", body: "Verified NGOs, food banks, shelters, schools, clinics." },
  { to: "/housing", icon: HomeIcon, title: "Sponsor a home", body: "Track every dollar and every board. One family at a time." },
  { to: "/opportunities?category=education", icon: GraduationCap, title: "Mentor or teach", body: "Share a skill weekly. Change a life monthly." },
  { to: "/opportunities?category=environment", icon: Sprout, title: "Restore the planet", body: "Cleanups, planting, gardens, water — with your local group." },
  { to: "/register?role=donor", icon: DollarSign, title: "Donate (mock)", body: "Give money, food, clothes, furniture, tech, professional services." },
];

export default function OfferHelp() {
  const { items: dbAppeals } = useArticles("appeal");
  const dbSlugs = new Set(dbAppeals.map((a) => a.slug));
  const appeals = [...dbAppeals, ...APPEALS.filter((a) => !dbSlugs.has(a.slug))];
  const [tab, setTab] = useState("browse");

  return (
    <div data-testid="offer-help-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
      <div className="overline mb-3">OFFER HELP</div>
      <h1 className="font-display text-4xl md:text-5xl max-w-2xl">Pick a way you&rsquo;d like to help today.</h1>
      <p className="mt-6 max-w-xl text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
        Every act — a Saturday shift, a mentoring hour, a roof panel sponsored — is verified and celebrated. Choose one below and start.
      </p>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b" style={{ borderColor: "var(--wofy-border)" }}>
        <button data-testid="offer-tab-browse" onClick={() => setTab("browse")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${tab === "browse" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "browse" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Ways to help</button>
        <button data-testid="offer-tab-submit" onClick={() => setTab("submit")} className={`px-4 py-2 text-sm font-mono uppercase tracking-wider transition-colors ${tab === "submit" ? "border-b-2" : "opacity-60 hover:opacity-100"}`} style={tab === "submit" ? { borderColor: "var(--wofy-primary)", color: "var(--wofy-primary)" } : {}}>Offer your help</button>
      </div>

      {tab === "submit" ? (
        <div className="mt-10 max-w-3xl">
          <PublicSubmissionForm type="help_offer" />
        </div>
      ) : (
        <>

      {appeals.length > 0 && (
        <section data-testid="emergency-appeals" className="mt-14">
          <div className="flex items-center gap-3 mb-6">
            <span
              className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-wider"
              style={{ background: "#B23A3A", color: "#FDFBF7" }}
            >
              <AlertTriangle className="w-3 h-3" /> Emergency appeals
            </span>
            <span className="wofy-divider flex-1" />
          </div>

          <div className="grid gap-6">
            {appeals.map((a) => (
              <Link
                key={a.slug}
                to={`/offer-help/${a.slug}`}
                data-testid={`appeal-${a.slug}`}
                className="wofy-card group !p-0 overflow-hidden border-2"
                style={{ borderColor: "#B23A3A" }}
              >
                <div className="relative p-8 md:p-10">
                  <span aria-hidden className="pointer-events-none absolute -top-16 -right-10 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: "#B23A3A" }} />
                  <div className="relative grid md:grid-cols-12 gap-6 md:gap-10 items-center">
                    <div className="md:col-span-8">
                      <div className="overline" style={{ color: "#B23A3A" }}>{a.overline}</div>
                      <div className="font-display text-2xl md:text-3xl mt-3 leading-tight">{a.title}</div>
                      <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>{a.subtitle}</p>
                      <div className="mt-5 inline-flex items-center gap-1 text-sm font-medium" style={{ color: "var(--wofy-primary)" }}>
                        Read the appeal &amp; donate <ArrowUpRight className="w-4 h-4" />
                      </div>
                    </div>
                    <div className="md:col-span-4 grid grid-cols-2 gap-4">
                      {(a.stats || []).slice(0, 4).map((s, i) => (
                        <div key={`${a.slug}-stat-${i}`}>
                          <div className="font-display text-xl md:text-2xl" style={{ color: "#B23A3A" }}>{s.k}</div>
                          <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>{s.v}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {WAYS.map((w) => (
          <Link key={w.title} to={w.to} data-testid={`offer-${w.title.toLowerCase().replace(/\s/g, "-")}`} className="wofy-card group">
            <w.icon className="w-6 h-6" style={{ color: "var(--wofy-primary)" }} />
            <div className="mt-6 font-display text-2xl leading-snug">{w.title}</div>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{w.body}</p>
            <div className="mt-6 text-sm underline underline-offset-4">Get started &rarr;</div>
          </Link>
        ))}
      </div>
        </>
      )}
    </div>
  );
}
